import type { WriteOffDiscrepancy, WriteOffMaterial } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { extractFailedSources, statusLabel, statusTone } from '../helpers';
import {
  Err,
  FailedSourcesBanner,
  FailedSourcesList,
  FailedSourcesTitle,
  Muted,
  ObjName,
  Row,
  TableTitle,
} from '../styles';
import { DiscrepanciesTable } from './DiscrepanciesTable';
import { MaterialsTable } from './MaterialsTable';

export interface WriteOffDetailData {
  check: {
    id: string;
    object_name: string;
    period?: string | null;
    status: string;
    summary?: unknown | null;
    contract_id?: string | null;
    project_id?: string | null;
    error_message?: string | null;
    user_id?: string | null;
  };
  sources: unknown[];
  norm_materials: WriteOffMaterial[];
  fact_materials: WriteOffMaterial[];
  discrepancies: WriteOffDiscrepancy[];
}

export function CheckDetailCard({
  detail,
  can_write,
  isAuthor,
  exporting,
  sendingRequest,
  requestNotice,
  exportError,
  onExport,
  onSendOverspendRequest,
  onDelete,
}: {
  detail: WriteOffDetailData;
  can_write: boolean;
  isAuthor: boolean;
  exporting: boolean;
  sendingRequest: boolean;
  requestNotice: string | null;
  exportError: string | null;
  onExport: (id: string) => void;
  onSendOverspendRequest: (kind: 'legal_claim' | 'budget_limit_exceeded') => void;
  onDelete: (id: string) => void;
}) {
  const { failedSources, totalFiles, hasUnparsedKs2 } = extractFailedSources(detail.check.summary);
  const incompleteKs2Hint =
    'Недоступно: часть актов КС-2 не разобрана, нормативный расход посчитан по неполным данным';
  return (
    <Card>
      <Row>
        <div>
          <ObjName>{detail.check.object_name}</ObjName>
          <Muted>
            {detail.check.period ? `${detail.check.period} · ` : ''}
            источников: {detail.sources.length} · нормативных: {detail.norm_materials.length} ·
            фактических: {detail.fact_materials.length} · расхождений:{' '}
            {detail.discrepancies.length}
          </Muted>
        </div>
        <Row>
          <Badge $tone={statusTone(detail.check.status)}>
            {statusLabel(detail.check.status)}
          </Badge>
          {detail.check.status === 'completed' && (
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              onClick={() => onExport(detail.check.id)}
              disabled={exporting}
            >
              {exporting ? 'Готовлю…' : 'Сформировать М-29 (XLSX)'}
            </Button>
          )}
          {detail.check.status === 'completed' && detail.discrepancies.length > 0 && (
            <>
              <Button
                $variant="secondary"
                $size="sm"
                type="button"
                disabled={sendingRequest || !can_write || hasUnparsedKs2}
                title={
                  hasUnparsedKs2
                    ? incompleteKs2Hint
                    : !can_write
                      ? 'Только чтение'
                      : 'Отправить заявку на претензию по перерасходу материалов'
                }
                onClick={() => onSendOverspendRequest('legal_claim')}
              >
                {sendingRequest ? 'Отправляю…' : 'Претензия (Юристу)'}
              </Button>
              <Button
                $variant="secondary"
                $size="sm"
                type="button"
                disabled={sendingRequest || !can_write || hasUnparsedKs2}
                title={
                  hasUnparsedKs2
                    ? incompleteKs2Hint
                    : !can_write
                      ? 'Только чтение'
                      : 'Сообщить Экономисту о перерасходе материалов сверх норматива'
                }
                onClick={() => onSendOverspendRequest('budget_limit_exceeded')}
              >
                Риск бюджета (Экономисту)
              </Button>
            </>
          )}
          <Button
            $variant="ghost"
            $size="sm"
            type="button"
            disabled={!can_write || !isAuthor}
            title={
              !can_write
                ? 'Только чтение'
                : !isAuthor
                  ? 'Удалить расчёт может только его автор'
                  : undefined
            }
            onClick={() => onDelete(detail.check.id)}
          >
            Удалить
          </Button>
        </Row>
      </Row>

      {failedSources.length > 0 && (
        <FailedSourcesBanner role="alert">
          <FailedSourcesTitle>
            Не удалось разобрать файлов: {failedSources.length}
            {totalFiles !== null ? ` из ${totalFiles}` : ''}. Сличение М-29 выполнено только по
            остальным документам — перепроверьте перечисленные источники:
          </FailedSourcesTitle>
          <FailedSourcesList>
            {failedSources.map((src, idx) => (
              <li key={`${src}-${idx}`}>{src}</li>
            ))}
          </FailedSourcesList>
        </FailedSourcesBanner>
      )}

      {requestNotice && <Muted $mt={8}>{requestNotice}</Muted>}
      {detail.check.status === 'failed' && detail.check.error_message && (
        <Err $mt={8}>{detail.check.error_message}</Err>
      )}
      {exportError && <Err $mt={8}>{exportError}</Err>}

      <TableTitle>
        Расхождения М-29 <Badge $tone="neutral">{detail.discrepancies.length}</Badge>
      </TableTitle>
      <DiscrepanciesTable items={detail.discrepancies} checkId={detail.check.id} />

      <TableTitle>
        Нормативный расход <Badge $tone="neutral">{detail.norm_materials.length}</Badge>
      </TableTitle>
      <MaterialsTable items={detail.norm_materials} kind="norm" />

      <TableTitle>
        Фактическое списание <Badge $tone="neutral">{detail.fact_materials.length}</Badge>
      </TableTitle>
      <MaterialsTable items={detail.fact_materials} kind="fact" />
    </Card>
  );
}
